import { useEffect, useState, useCallback } from "react";
import DOMPurify from "dompurify";
import {
  BookOpen,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Check,
  X,
  Bell,
  Save,
  History,
  RotateCcw,
  Eye,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import RichTextEditor from "@/components/RichTextEditor";
import { useAuth } from "@/context/AuthContext";
import { PreviewEnvNotice } from "@/components/PreviewEnvNotice";
import {
  handbookGet,
  handbookAdminSections,
  handbookCreateSection,
  handbookUpdateSection,
  handbookDeleteSection,
  handbookReorder,
  handbookPending,
  handbookApprove,
  handbookReject,
  handbookSectionVersions,
  handbookSectionRestore,
  handbookSectionVersionDelete,
} from "@/lib/api";
import { toast } from "sonner";

function Html({ html }) {
  return (
    <div
      className="handbook-prose"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html || "") }}
    />
  );
}

export default function Handbuch() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [pending, setPending] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Versionierung (analog zu den E-Mail-Vorlagen)
  const [versions, setVersions] = useState({}); // sectionId -> Versionen[]
  const [openVerSec, setOpenVerSec] = useState(null); // Abschnitt mit offenem Protokoll
  const [verRev, setVerRev] = useState({}); // erzwingt Neu-Mount des Editors nach Restore
  const [verPreview, setVerPreview] = useState(null); // {title, content}
  const [delVer, setDelVer] = useState(null); // {sectionId, version}

  const fmtTs = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  const loadVersions = useCallback(async (id) => {
    try {
      const data = await handbookSectionVersions(id);
      setVersions((v) => ({ ...v, [id]: data.versions || [] }));
    } catch {
      /* ignore */
    }
  }, []);

  const toggleVersions = (id) => {
    if (openVerSec === id) {
      setOpenVerSec(null);
      return;
    }
    setOpenVerSec(id);
    loadVersions(id);
  };

  const restoreVersion = async (sectionId, ver) => {
    try {
      const data = await handbookSectionRestore(sectionId, ver.id);
      if (data.section) {
        setDrafts((d) => ({
          ...d,
          [sectionId]: { title: data.section.title, content: data.section.content },
        }));
        setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, ...data.section } : s)));
        setVerRev((r) => ({ ...r, [sectionId]: (r[sectionId] || 0) + 1 }));
      }
      setVersions((v) => ({ ...v, [sectionId]: data.versions || [] }));
      toast.success("Version wiederhergestellt.");
    } catch {
      toast.error("Wiederherstellen fehlgeschlagen.");
    }
  };

  const deleteVersion = async () => {
    if (!delVer) return;
    try {
      const data = await handbookSectionVersionDelete(delVer.sectionId, delVer.version.id);
      setVersions((v) => ({ ...v, [delVer.sectionId]: data.versions || [] }));
      toast.success("Version gelöscht.");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    } finally {
      setDelVer(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = isAdmin ? await handbookAdminSections() : await handbookGet();
      setSections(data.sections || []);
    } catch {
      toast.error("Handbuch konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  const loadPending = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const data = await handbookPending();
      setPending(data.pending || []);
      setPendingCount(data.count || 0);
    } catch {
      /* ignore */
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
    loadPending();
  }, [load, loadPending]);

  const startEdit = () => {
    const d = {};
    sections.forEach((s) => (d[s.id] = { title: s.title, content: s.content }));
    setDrafts(d);
    setEditMode(true);
  };

  const saveSection = async (s) => {
    setSavingId(s.id);
    try {
      await handbookUpdateSection(s.id, drafts[s.id]);
      toast.success("Abschnitt gespeichert.");
      await load();
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSavingId(null);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await handbookDeleteSection(confirmDelete.id);
      toast.success("Abschnitt gelöscht.");
      setConfirmDelete(null);
      await load();
      if (editMode) {
        const data = await handbookAdminSections();
        const d = {};
        (data.sections || []).forEach((s) => (d[s.id] = { title: s.title, content: s.content }));
        setDrafts(d);
      }
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    } finally {
      setDeleting(false);
    }
  };

  const addSection = async () => {
    try {
      await handbookCreateSection({ title: "Neuer Abschnitt", content: "<p>Inhalt …</p>" });
      toast.success("Abschnitt hinzugefügt.");
      const data = await handbookAdminSections();
      setSections(data.sections || []);
      const d = {};
      (data.sections || []).forEach((s) => (d[s.id] = { title: s.title, content: s.content }));
      setDrafts(d);
    } catch {
      toast.error("Hinzufügen fehlgeschlagen.");
    }
  };

  const move = async (idx, dir) => {
    const next = [...sections];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setSections(next);
    try {
      await handbookReorder(next.map((s) => s.id));
    } catch {
      toast.error("Reihenfolge konnte nicht gespeichert werden.");
      load();
    }
  };

  const approve = async (p) => {
    try {
      await handbookApprove(p.id);
      toast.success("Änderung freigegeben.");
      await loadPending();
      await load();
    } catch {
      toast.error("Freigabe fehlgeschlagen.");
    }
  };

  const reject = async (p) => {
    try {
      await handbookReject(p.id);
      toast.success("Vorschlag verworfen.");
      await loadPending();
    } catch {
      toast.error("Aktion fehlgeschlagen.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="handbuch-page">
      {isAdmin && (
        <div className="mb-6">
          <PreviewEnvNotice area="Handbuch-Inhalte" />
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <BookOpen className="h-7 w-7 text-primary" />
          <h1 className="text-3xl font-bold">Handbuch</h1>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button
              variant={pendingCount > 0 ? "default" : "secondary"}
              className="gap-2"
              onClick={() => setReviewOpen(true)}
              data-testid="handbuch-review-btn"
            >
              <Bell className="h-4 w-4" />
              Änderungen prüfen
              {pendingCount > 0 && (
                <span
                  className="ml-1 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-xs h-5 min-w-5 px-1"
                  data-testid="handbuch-pending-badge"
                >
                  {pendingCount}
                </span>
              )}
            </Button>
            {editMode ? (
              <Button
                variant="secondary"
                className="gap-2"
                onClick={() => setEditMode(false)}
                data-testid="handbuch-edit-done"
              >
                <Check className="h-4 w-4" /> Fertig
              </Button>
            ) : (
              <Button className="gap-2" onClick={startEdit} data-testid="handbuch-edit-toggle">
                <Pencil className="h-4 w-4" /> Bearbeiten
              </Button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-12" data-testid="handbuch-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Handbuch wird geladen…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
          {/* Menüführung / Inhaltsverzeichnis */}
          <nav
            className="lg:sticky lg:top-[120px] lg:self-start lg:max-h-[calc(100vh-140px)] lg:overflow-y-auto"
            data-testid="handbuch-toc"
          >
            <div className="bg-white border border-border rounded-lg p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                Inhalt
              </div>
              <ul className="space-y-1">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#sec-${s.slug}`}
                      data-testid={`toc-${s.slug}`}
                      className="block text-sm text-foreground/80 hover:text-primary hover:bg-muted rounded px-2 py-1 transition-colors"
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          {/* Inhalt */}
          <div className="min-w-0 space-y-6">
            {editMode && (
              <Button variant="secondary" className="gap-2" onClick={addSection} data-testid="handbuch-add-section">
                <Plus className="h-4 w-4" /> Abschnitt hinzufügen
              </Button>
            )}
            {sections.map((s, idx) => (
              <article
                key={s.id}
                id={`sec-${s.slug}`}
                className="bg-white border border-border rounded-lg p-6 scroll-mt-4"
                data-testid={`handbuch-section-${s.slug}`}
              >
                {editMode ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Input
                        value={drafts[s.id]?.title ?? ""}
                        onChange={(e) =>
                          setDrafts((d) => ({ ...d, [s.id]: { ...d[s.id], title: e.target.value } }))
                        }
                        className="font-semibold"
                        data-testid={`section-title-${s.slug}`}
                      />
                      <Button variant="ghost" size="icon" onClick={() => move(idx, -1)} title="Nach oben">
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => move(idx, 1)} title="Nach unten">
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        onClick={() => setConfirmDelete(s)}
                        title="Löschen"
                        data-testid={`section-delete-${s.slug}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <RichTextEditor
                      key={`${s.id}-${verRev[s.id] || 0}`}
                      value={drafts[s.id]?.content ?? ""}
                      onChange={(html) =>
                        setDrafts((d) => ({ ...d, [s.id]: { ...d[s.id], content: html } }))
                      }
                      testId={`section-content-${s.slug}`}
                    />
                    <Button
                      className="gap-2"
                      onClick={() => saveSection(s)}
                      disabled={savingId === s.id}
                      data-testid={`section-save-${s.slug}`}
                    >
                      {savingId === s.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      Speichern
                    </Button>

                    {/* Änderungsprotokoll (Versionierung wie bei den Mails) */}
                    <div className="border border-border rounded-md mt-2" data-testid={`section-versions-${s.slug}`}>
                      <button
                        type="button"
                        onClick={() => toggleVersions(s.id)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted/50 transition-colors"
                        data-testid={`section-versions-toggle-${s.slug}`}
                      >
                        {openVerSec === s.id ? (
                          <ChevronDown className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5" />
                        )}
                        <History className="h-3.5 w-3.5" /> Änderungsprotokoll
                        {(versions[s.id]?.length || 0) > 0 && (
                          <span className="text-muted-foreground">({versions[s.id].length})</span>
                        )}
                      </button>
                      {openVerSec === s.id && (
                        <div className="border-t border-border divide-y divide-border">
                          {(versions[s.id]?.length || 0) === 0 ? (
                            <p className="px-3 py-3 text-xs text-muted-foreground">
                              Noch keine früheren Versionen. Beim nächsten Speichern wird der
                              vorherige Stand hier abgelegt.
                            </p>
                          ) : (
                            versions[s.id].map((v) => (
                              <div
                                key={v.id}
                                className="flex items-center justify-between gap-2 px-3 py-2"
                                data-testid={`section-version-${v.id}`}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-sm font-medium truncate">{v.title || "Abschnitt"}</span>
                                  <span className="text-xs text-muted-foreground shrink-0">
                                    {fmtTs(v.created_at)}
                                  </span>
                                  {v.source === "agent" && (
                                    <span className="text-[10px] rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5 shrink-0">
                                      Agent
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    title="Vorschau"
                                    onClick={() => setVerPreview({ title: v.title, content: v.content })}
                                    data-testid={`section-version-preview-${v.id}`}
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-muted-foreground"
                                    title="Wiederherstellen"
                                    onClick={() => restoreVersion(s.id, v)}
                                    data-testid={`section-version-restore-${v.id}`}
                                  >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-muted-foreground hover:text-red-600"
                                    title="Version löschen"
                                    onClick={() => setDelVer({ sectionId: s.id, version: v })}
                                    data-testid={`section-version-delete-${v.id}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="font-heading text-xl font-semibold mb-3 pb-2 border-b border-border">
                      {s.title}
                    </h2>
                    <Html html={s.content} />
                  </>
                )}
              </article>
            ))}
          </div>
        </div>
      )}

      {/* Löschen bestätigen (ersetzt window.confirm – funktioniert auch im Vorschau-iframe) */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Abschnitt löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Abschnitt „{confirmDelete?.title}" wirklich löschen? Dies kann nicht
              rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                doDelete();
              }}
              disabled={deleting}
              data-testid="delete-confirm"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Review-Dialog für Agenten-Vorschläge */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="handbuch-review-dialog">
          <DialogHeader>
            <DialogTitle>Offene Änderungsvorschläge ({pending.length})</DialogTitle>
          </DialogHeader>
          {pending.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6">
              Keine offenen Vorschläge. Sobald die Funktionsweise angepasst wird, erscheinen hier
              automatische Vorschläge zur Prüfung und Freigabe.
            </p>
          ) : (
            <div className="space-y-4">
              {pending.map((p) => (
                <div key={p.id} className="border border-border rounded-lg p-4" data-testid={`pending-${p.slug}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          p.change_type === "added"
                            ? "bg-green-100 text-green-700"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {p.change_type === "added" ? "Neu" : "Geändert"}
                      </span>
                      <span className="font-medium">{p.title}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="sm" className="gap-1" onClick={() => approve(p)} data-testid={`approve-${p.slug}`}>
                        <Check className="h-4 w-4" /> Freigeben
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="gap-1"
                        onClick={() => reject(p)}
                        data-testid={`reject-${p.slug}`}
                      >
                        <X className="h-4 w-4" /> Verwerfen
                      </Button>
                    </div>
                  </div>
                  {p.note && <p className="text-xs text-muted-foreground mb-2">Notiz: {p.note}</p>}
                  <div className="bg-muted/50 rounded p-3 max-h-48 overflow-y-auto">
                    <Html html={p.content} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Versions-Vorschau */}
      <Dialog open={!!verPreview} onOpenChange={(o) => !o && setVerPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="section-version-preview-dialog">
          <DialogHeader>
            <DialogTitle>Vorschau: {verPreview?.title || "Abschnitt"}</DialogTitle>
          </DialogHeader>
          <div className="bg-white border border-border rounded-md p-4">
            <Html html={verPreview?.content} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Version löschen – Rückfrage */}
      <AlertDialog open={!!delVer} onOpenChange={(o) => !o && setDelVer(null)}>
        <AlertDialogContent data-testid="section-version-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Version löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diese Version{delVer?.version?.created_at ? ` vom ${fmtTs(delVer.version.created_at)}` : ""}{" "}
              wirklich unwiderruflich löschen? Der aktuell gespeicherte Abschnitt bleibt unberührt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="section-version-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                deleteVersion();
              }}
              data-testid="section-version-delete-confirm"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
